import type { MouseEvent } from "react"
import { useState } from "react"
import {
	NodeViewContent,
	NodeViewWrapper,
	ReactNodeViewRenderer,
} from "@tiptap/react"
import { IconSlidersOutline18 as SlidersIcon } from "nucleo-ui-outline-18"

import { ExpandableExtension } from "@productlane/lib/src/tiptap/extensions/mintlify/expandable"
import { DEFAULT_EXPANDABLE_TITLE } from "@productlane/lib/src/tiptap/extensions/mintlify/fields-html"
import { IconButton } from "@productlane/ui"
import {
	DropdownMenu,
	DropdownMenuCheckboxItem,
} from "@productlane/ui/src/components/internal/dropdown"

import type { NodeViewProps } from "@tiptap/react"

const stop = (event: MouseEvent) => event.stopPropagation()

// A real <details> would hide the child fields being edited and fight
// ProseMirror over `open`, so the editor draws a div that toggles in place.
function ExpandableNodeView({ node, editor, updateAttributes }: NodeViewProps) {
	// Open while editing, so the child fields stay in reach.
	const [open, setOpen] = useState(true)
	const [menuOpen, setMenuOpen] = useState(false)
	const readOnly = !editor.isEditable
	const title = typeof node.attrs.title === "string" ? node.attrs.title : ""
	const defaultOpen = node.attrs.defaultOpen === true

	return (
		<NodeViewWrapper
			data-type="expandable"
			className="pl-expandable"
			data-open={String(open)}
		>
			<div
				className="pl-expandable-summary cursor-default"
				contentEditable={false}
				suppressContentEditableWarning
				role="button"
				tabIndex={0}
				aria-expanded={open}
				onClick={() => setOpen((value) => !value)}
				onKeyDown={(event) => {
					if (event.target !== event.currentTarget) return
					if (event.key !== "Enter" && event.key !== " ") return
					event.preventDefault()
					setOpen((value) => !value)
				}}
			>
				<span className="pl-expandable-icon" aria-hidden="true" />
				<span className="pl-expandable-title">
					<input
						className="pl-field-input"
						value={title}
						placeholder={DEFAULT_EXPANDABLE_TITLE}
						aria-label="Expandable title"
						readOnly={readOnly}
						spellCheck={false}
						size={Math.max(1, (title || DEFAULT_EXPANDABLE_TITLE).length)}
						onClick={stop}
						onChange={(event) =>
							updateAttributes({ title: event.target.value })
						}
					/>
				</span>
				{readOnly ? null : (
					<span
						className="pl-expandable-controls"
						data-open={menuOpen}
						role="presentation"
						onClick={stop}
					>
						<DropdownMenu
							open={menuOpen}
							onOpenChange={setMenuOpen}
							className="w-44"
							trigger={
								<IconButton
									variant="ghost"
									size="sm"
									Icon={SlidersIcon}
									aria-label="Expandable settings"
									className="flex-none cursor-default"
								/>
							}
						>
							<DropdownMenuCheckboxItem
								checked={defaultOpen}
								onCheckedChange={(checked) =>
									updateAttributes({ defaultOpen: checked === true })
								}
							>
								Open by default
							</DropdownMenuCheckboxItem>
						</DropdownMenu>
					</span>
				)}
			</div>
			<NodeViewContent className="pl-expandable-content" />
		</NodeViewWrapper>
	)
}

export const ExpandableWithNodeView = ExpandableExtension.extend({
	addNodeView() {
		return ReactNodeViewRenderer(ExpandableNodeView)
	},
})
